/**
 * End-to-end encryption primitives (WebCrypto; identical in browsers and Node ≥ 20).
 *
 * - Each encrypted scope (a project or a single note) has a random 256-bit AES-GCM key.
 * - The scope key is stored on the server only WRAPPED: encrypted with a key derived from a
 *   passphrase (PBKDF2-SHA256, 600 000 iterations) or from a random recovery key.
 * - Every ciphertext is bound to its scope, purpose and object with AES-GCM associated data,
 *   so blobs cannot be swapped between notes, titles, files or comments undetected.
 * - The server receives only ciphertext, salts, iteration counts and a one-way key proof.
 * - A scope key can also be wrapped for a member's personal public key (see below), so sharing
 *   an encrypted notebook or project shares its key with exactly the people it is shared with.
 */

export const KDF_NAME = "PBKDF2-SHA256";
export const KDF_ITERATIONS = 600_000;
const BLOB_VERSION = 1;
const IV_BYTES = 12;

export type CipherPurpose = "key" | "title" | "update" | "snapshot" | "version" | "comment" | "comment-anchor" | "file" | "file-meta";

export interface KeySlotData {
  kind: "passphrase" | "recovery";
  kdf: string;
  iterations: number;
  salt: string;
  wrappedKey: string;
}

export class WrongKeyError extends Error {
  constructor() {
    super("The passphrase or recovery key is not correct.");
  }
}

export class DecryptError extends Error {
  constructor() {
    super("This content could not be decrypted with the unlocked key.");
  }
}

const subtle = () => {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error("WebCrypto is not available (use a secure context: https or localhost).");
  return s;
};

const te = new TextEncoder();
const td = new TextDecoder();

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return b;
}

export function toBase64(bytes: Uint8Array): string {
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

const buf = (u: Uint8Array) => u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer;

export function aad(scopeId: string, purpose: CipherPurpose, objectId: string) {
  return te.encode(`brainlog-claude:v1:${scopeId}:${purpose}:${objectId}`);
}

/** Fresh random scope key: raw bytes (to wrap) and an AES-GCM CryptoKey (to use). */
export async function generateScopeKey() {
  const raw = randomBytes(32);
  const key = await importScopeKey(raw);
  return { raw, key };
}

export async function importScopeKey(raw: Uint8Array) {
  return subtle().importKey("raw", buf(raw), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

async function deriveKek(secret: string, salt: Uint8Array, iterations: number) {
  const base = await subtle().importKey("raw", buf(te.encode(secret.normalize("NFKC"))), "PBKDF2", false, ["deriveKey"]);
  return subtle().deriveKey({ name: "PBKDF2", hash: "SHA-256", salt: buf(salt), iterations }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function encryptBytes(key: CryptoKey, plain: Uint8Array, associated: Uint8Array): Promise<Uint8Array> {
  const iv = randomBytes(IV_BYTES);
  const ct = new Uint8Array(await subtle().encrypt({ name: "AES-GCM", iv: buf(iv), additionalData: buf(associated) }, key, buf(plain)));
  const out = new Uint8Array(1 + IV_BYTES + ct.length);
  out[0] = BLOB_VERSION;
  out.set(iv, 1);
  out.set(ct, 1 + IV_BYTES);
  return out;
}

export async function decryptBytes(key: CryptoKey, blob: Uint8Array, associated: Uint8Array): Promise<Uint8Array> {
  if (blob[0] !== BLOB_VERSION || blob.length < 1 + IV_BYTES + 16) throw new DecryptError();
  try {
    const iv = blob.subarray(1, 1 + IV_BYTES);
    const ct = blob.subarray(1 + IV_BYTES);
    return new Uint8Array(await subtle().decrypt({ name: "AES-GCM", iv: buf(iv), additionalData: buf(associated) }, key, buf(ct)));
  } catch {
    throw new DecryptError();
  }
}

export async function encryptText(key: CryptoKey, text: string, associated: Uint8Array) {
  return toBase64(await encryptBytes(key, te.encode(text), associated));
}

export async function decryptText(key: CryptoKey, blob: string, associated: Uint8Array) {
  return td.decode(await decryptBytes(key, fromBase64(blob), associated));
}

/** Wrap the raw scope key with a passphrase (or recovery key). */
export async function wrapScopeKey(raw: Uint8Array, secret: string, scopeId: string, kind: KeySlotData["kind"], iterations = KDF_ITERATIONS): Promise<KeySlotData> {
  const salt = randomBytes(16);
  const kek = await deriveKek(secret, salt, iterations);
  const wrapped = await encryptBytes(kek, raw, aad(scopeId, "key", kind));
  return { kind, kdf: KDF_NAME, iterations, salt: toBase64(salt), wrappedKey: toBase64(wrapped) };
}

/** Unwrap a scope key; throws WrongKeyError for a wrong passphrase. */
export async function unwrapScopeKey(slot: KeySlotData, secret: string, scopeId: string): Promise<{ raw: Uint8Array; key: CryptoKey }> {
  if (slot.kdf !== KDF_NAME) throw new Error(`Unsupported key derivation ${slot.kdf}`);
  const kek = await deriveKek(secret, fromBase64(slot.salt), slot.iterations);
  let raw: Uint8Array;
  try {
    raw = await decryptBytes(kek, fromBase64(slot.wrappedKey), aad(scopeId, "key", slot.kind));
  } catch {
    throw new WrongKeyError();
  }
  return { raw, key: await importScopeKey(raw) };
}

/** One-way proof of key possession (lets the server gate destructive operations). */
export async function keyProof(raw: Uint8Array, scopeId: string) {
  const data = new Uint8Array([...raw, ...te.encode(`:${scopeId}:proof`)]);
  return toBase64(new Uint8Array(await subtle().digest("SHA-256", buf(data))));
}

// Personal keys and member key slots ---------------------------------------------------
//
// Each person can create a personal key pair (ECDH P-256) in their browser. The public key is
// stored in clear; the private key only wrapped by the person's own passphrase (and optionally a
// recovery key), exactly like a scope key. Sharing an encrypted notebook or project with someone
// wraps its scope key for their public key (ephemeral ECDH + HKDF-SHA-256 + AES-GCM), so they
// open it with their own passphrase and nobody has to pass a passphrase around.

export const USER_KEY_ALGORITHM = "ECDH-P256";
export const MEMBER_SLOT_KDF = "ECDH-P256-HKDF-SHA256";

/** A scope key wrapped for one member's public key. */
export interface MemberSlotData {
  kind: "member";
  recipientUserId: string;
  kdf: string;
  iterations: number;
  salt: string;
  ephemeralPublicKey: string;
  wrappedKey: string;
}

export type ScopeSlotData = KeySlotData | MemberSlotData;

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
const userScope = (userId: string) => `user:${userId.toLowerCase()}`;

/** Fresh personal key pair: the public key (SPKI, base64) and the private key bytes (PKCS#8, to wrap). */
export async function generateUserKeyPair() {
  const pair = (await subtle().generateKey(ECDH, true, ["deriveBits"])) as { publicKey: CryptoKey; privateKey: CryptoKey };
  const spki = new Uint8Array(await subtle().exportKey("spki", pair.publicKey));
  const pkcs8 = new Uint8Array(await subtle().exportKey("pkcs8", pair.privateKey));
  return { publicKey: toBase64(spki), pkcs8 };
}

export async function importUserPrivateKey(pkcs8: Uint8Array) {
  return subtle().importKey("pkcs8", buf(pkcs8), ECDH, false, ["deriveBits"]);
}

async function importUserPublicKey(spkiB64: string) {
  return subtle().importKey("spki", buf(fromBase64(spkiB64)), ECDH, false, []);
}

/** Short, comparable fingerprint of a public key (e.g. "3F2A 91C0 …") to check out of band. */
export async function publicKeyFingerprint(spkiB64: string) {
  const d = new Uint8Array(await subtle().digest("SHA-256", buf(fromBase64(spkiB64))));
  return [...d.subarray(0, 10)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()
    .replace(/(.{4})(?=.)/g, "$1 ");
}

/** Wrap a personal private key with the person's passphrase (or their personal recovery key). */
export async function wrapUserPrivateKey(pkcs8: Uint8Array, secret: string, userId: string, kind: KeySlotData["kind"], iterations = KDF_ITERATIONS): Promise<KeySlotData> {
  const salt = randomBytes(16);
  const kek = await deriveKek(secret, salt, iterations);
  const wrapped = await encryptBytes(kek, pkcs8, aad(userScope(userId), "key", kind));
  return { kind, kdf: KDF_NAME, iterations, salt: toBase64(salt), wrappedKey: toBase64(wrapped) };
}

/** Unwrap a personal private key; throws WrongKeyError for a wrong passphrase or recovery key. */
export async function unwrapUserPrivateKey(slot: KeySlotData, secret: string, userId: string) {
  if (slot.kdf !== KDF_NAME) throw new Error(`Unsupported key derivation ${slot.kdf}`);
  const kek = await deriveKek(secret, fromBase64(slot.salt), slot.iterations);
  let pkcs8: Uint8Array;
  try {
    pkcs8 = await decryptBytes(kek, fromBase64(slot.wrappedKey), aad(userScope(userId), "key", slot.kind));
  } catch {
    throw new WrongKeyError();
  }
  return { pkcs8, privateKey: await importUserPrivateKey(pkcs8) };
}

/** One-way proof of holding the personal private key (required to replace its wrapped copies). */
export async function userKeyProof(pkcs8: Uint8Array, userId: string) {
  const data = new Uint8Array([...pkcs8, ...te.encode(`:${userScope(userId)}:proof`)]);
  return toBase64(new Uint8Array(await subtle().digest("SHA-256", buf(data))));
}

const memberInfo = (scopeId: string, userId: string) => te.encode(`brainlog-claude:v1:member-key:${scopeId}:${userId.toLowerCase()}`);
const memberObject = (userId: string) => `member:${userId.toLowerCase()}`;

async function memberKek(privateKey: CryptoKey, publicKey: CryptoKey, salt: Uint8Array, info: Uint8Array) {
  const bits = await subtle().deriveBits({ name: "ECDH", public: publicKey }, privateKey, 256);
  const hkdf = await subtle().importKey("raw", bits, "HKDF", false, ["deriveKey"]);
  return subtle().deriveKey({ name: "HKDF", hash: "SHA-256", salt: buf(salt), info: buf(info) }, hkdf, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/** Wrap a scope key for one member (their public key). Only their private key can unwrap it. */
export async function wrapScopeKeyForMember(raw: Uint8Array, scopeId: string, recipientUserId: string, recipientPublicKey: string): Promise<MemberSlotData> {
  const eph = (await subtle().generateKey(ECDH, true, ["deriveBits"])) as { publicKey: CryptoKey; privateKey: CryptoKey };
  const salt = randomBytes(16);
  const kek = await memberKek(eph.privateKey, await importUserPublicKey(recipientPublicKey), salt, memberInfo(scopeId, recipientUserId));
  const wrapped = await encryptBytes(kek, raw, aad(scopeId, "key", memberObject(recipientUserId)));
  const epk = new Uint8Array(await subtle().exportKey("spki", eph.publicKey));
  return {
    kind: "member",
    recipientUserId: recipientUserId.toLowerCase(),
    kdf: MEMBER_SLOT_KDF,
    iterations: 0,
    salt: toBase64(salt),
    ephemeralPublicKey: toBase64(epk),
    wrappedKey: toBase64(wrapped),
  };
}

/** Unwrap a scope key that was shared with this person. Throws WrongKeyError if it was not wrapped for this key. */
export async function unwrapMemberSlot(slot: MemberSlotData, privateKey: CryptoKey, scopeId: string, userId: string) {
  if (slot.kdf !== MEMBER_SLOT_KDF) throw new Error(`Unsupported key wrapping ${slot.kdf}`);
  let raw: Uint8Array;
  try {
    const kek = await memberKek(privateKey, await importUserPublicKey(slot.ephemeralPublicKey), fromBase64(slot.salt), memberInfo(scopeId, userId));
    raw = await decryptBytes(kek, fromBase64(slot.wrappedKey), aad(scopeId, "key", memberObject(userId)));
  } catch {
    throw new WrongKeyError();
  }
  return { raw, key: await importScopeKey(raw) };
}

const B32 = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
/** Human-friendly random recovery key, e.g. "K7QM-2XRA-…" (25 groups of entropy ≈ 125 bits). */
export function generateRecoveryKey() {
  const bytes = randomBytes(25);
  const chars = [...bytes].map((b) => B32[b % 32]);
  const groups: string[] = [];
  for (let i = 0; i < chars.length; i += 5) groups.push(chars.slice(i, i + 5).join(""));
  return groups.join("-");
}

export function normalizeRecoveryKey(input: string) {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/(.{5})(?=.)/g, "$1-");
}

/** Rough passphrase strength guidance (not a guarantee). */
export function passphraseProblems(p: string): string[] {
  const problems: string[] = [];
  if (p.length < 12) problems.push("Use at least 12 characters (a few unrelated words work well).");
  if (/^(.)\1+$/.test(p)) problems.push("Avoid repeated characters.");
  return problems;
}

export const ENCRYPTED_TITLE_PLACEHOLDER = "Encrypted note";
export const ENCRYPTED_FILENAME_PLACEHOLDER = "encrypted-file";
